import { createContext, useContext, useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const FulfillmentOrderDrawer = lazyWithReload(() => import('./FulfillmentOrderDrawer'), 'FulfillmentOrderDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { providerIfModuleOn } from '../../lib/hostModules';

/**
 * URL-driven `?orderId=<short>` (FO = Fulfillment Order).
 * API: useFulfillmentOrderDrawer() → { openOrder, closeOrder, version, bumpVersion, openOrderId }
 *
 * Provider OPTIONAL — host nào không có module triển khai thì không mount, và hook KHÔNG
 * throw: cnv-ui's chat pages (LinkUnfurl, EntityCards) gọi hook để bung drawer khi user
 * share link FO trong chat, không có drawer thì rơi về link trơn. Xem NO_PROVIDER.
 * (Core và cnvwork-saas hiện đều có mount — đừng tin dòng "SaaS không có" ở bản cũ.)
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC — chính là sự cố 18.09.2026: hoá đơn mở trong drawer bấm thẻ «Đơn triển
// khai» chết câm, vì CrmInvoiceDrawerProvider bọc NGOÀI provider này). Xem
// utils/drawerNoProvider.
const NO_PROVIDER = {
  openOrder: noProviderOpener('fulfillment'),
  closeOrder: () => {},
  version: 0,
  bumpVersion: () => {},
  openOrderId: null,
};

function FulfillmentOrderDrawerProviderOn({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const [version, setVersion] = useState(0);

  const openOrder = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('orderId') === short) return prev;
      const next = new URLSearchParams(prev);
      next.set('orderId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeOrder = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('orderId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('orderId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('fulfillment', openOrder), [openOrder]);

  return (
    <Ctx.Provider value={{ openOrder, closeOrder, version, bumpVersion, openOrderId: orderId }}>
      {children}
      {orderId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeOrder} />}>
          <FulfillmentOrderDrawer open={!!orderId} orderId={orderId} onClose={closeOrder} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useFulfillmentOrderDrawer() {
  // KHÔNG throw — lui về NO_PROVIDER khi Provider không nằm trên cây của người gọi.
  return useContext(Ctx) || NO_PROVIDER;
}

// Cụm CRM tắt ở host (`modules.crm = false` — Site Connect 12/10/2026) ⇒ Provider rỗng: không đăng ký popup, không
// đọc query param, không dựng drawer (nội dung drawer gọi hàng chục /crm/*). Xem lib/hostModules.
export const FulfillmentOrderDrawerProvider = providerIfModuleOn('crm', FulfillmentOrderDrawerProviderOn);
